import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { useHistory } from 'react-router-dom'
import Header from './Header'
import Footer from './footer'
import PageHead from './pageHead'
import PageSubHead from './pageSubHead'
import PageBody from './pageBody'
import { GetAction } from './GetAction'
import { PostAction } from './PostAction'
import RichTextEditor from '../manage/components/RichTextEditor'
import { hasModule } from '../manage/auth'
import { AUTH_EVENT } from '../utils/sessionAuth'

/** Keep a single URL path segment; spaces become hyphens. */
function sanitizePageUrlInput(value) {
  return String(value || '')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function slugFromTitle(title) {
  return sanitizePageUrlInput(title)
}

function applyTitleToForm(prev, nextTitle) {
  const nextSlug = slugFromTitle(nextTitle)
  const syncLabel = !prev.menu_label || prev.menu_label === prev.title
  const syncSub = !prev.subTitle || prev.subTitle === prev.title

  return {
    ...prev,
    title: nextTitle,
    menu_label: syncLabel ? nextTitle : prev.menu_label,
    subTitle: syncSub ? nextTitle : prev.subTitle,
    page_url: nextSlug,
  }
}

/**
 * Public CMS page shell with an in-place Edit modal for users who have
 * the Manage → Pages permission.
 */
function CmsPageScreen({
  pageId,
  pageUrl,
  contentClassName = '',
  showSubHead = true,
}) {
  const history = useHistory()
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [canEdit, setCanEdit] = useState(() => hasModule('pages'))
  const [showEdit, setShowEdit] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [form, setForm] = useState({
    title: '',
    menu_label: '',
    subTitle: '',
    content: '',
    page_url: '',
    status: 'Active',
    menu_location: 'none',
    menu_sequence: 0,
  })

  const refreshCanEdit = useCallback(() => {
    setCanEdit(hasModule('pages'))
  }, [])

  useEffect(() => {
    refreshCanEdit()
    window.addEventListener(AUTH_EVENT, refreshCanEdit)
    return () => window.removeEventListener(AUTH_EVENT, refreshCanEdit)
  }, [refreshCanEdit])

  const loadPage = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (pageId != null && pageId !== '') params.page_id = pageId
      else if (pageUrl) params.page_url = pageUrl
      else {
        setPage(null)
        return
      }
      const res = await GetAction('get', params, '/pages/page')
      setPage(res?.response || null)
    } finally {
      setLoading(false)
    }
  }, [pageId, pageUrl])

  useEffect(() => {
    loadPage()
  }, [loadPage])

  const openEdit = () => {
    if (!page) return
    const statusRaw = String(page.status || 'Active').toLowerCase()
    setForm({
      title: page.title || '',
      menu_label: page.menu_label || page.title || '',
      subTitle: page.subTitle || '',
      content: page.description || '',
      page_url: sanitizePageUrlInput(page.page_url || ''),
      status: statusRaw === 'inactive' || statusRaw === '0' ? 'Inactive' : 'Active',
      menu_location: page.menu_location || 'none',
      menu_sequence:
        page.menu_sequence != null && page.menu_sequence !== ''
          ? page.menu_sequence
          : 0,
    })
    setMessage(null)
    setShowEdit(true)
  }

  const urlPreview = useMemo(() => {
    const slug = sanitizePageUrlInput(form.page_url)
    if (slug) return `/${slug}`
    if (page?.page_id) return `/page/${page.page_id}`
    return '/page/{id}'
  }, [form.page_url, page])

  const onSave = async (e) => {
    e.preventDefault()
    if (!page?.page_id) return
    setSaving(true)
    setMessage(null)
    const payload = {
      page_id: page.page_id,
      title: form.title.trim(),
      menu_label: (form.menu_label || '').trim() || form.title.trim(),
      subTitle: form.subTitle.trim(),
      content: form.content,
      status: form.status,
      menu_location: form.menu_location,
      menu_sequence: Number(form.menu_sequence) || 0,
      page_url: sanitizePageUrlInput(form.page_url),
    }
    const data = await PostAction('post', payload, 'pages/addPage')
    if (data?.status === 'success') {
      setShowEdit(false)
      const newSlug = sanitizePageUrlInput(form.page_url)
      const oldSlug = sanitizePageUrlInput(page.page_url || '')
      if (newSlug && newSlug.toLowerCase() !== oldSlug.toLowerCase()) {
        history.push(`/${newSlug}`)
      } else {
        await loadPage()
      }
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save page.',
      })
    }
    setSaving(false)
  }

  const title = page?.title || ''
  const subtitle = page?.subTitle || ''
  const markup = page?.description || ''
  const staticMarkup = { __html: markup }
  const containerClass = ['container', contentClassName].filter(Boolean).join(' ')

  return (
    <>
      <Header />
      <PageHead title={title} />
      {showSubHead ? <PageSubHead title={subtitle} /> : null}

      {canEdit && page?.page_id ? (
        <div className="container cms-page-edit-bar">
          <button
            type="button"
            className="btn btn-sm cms-page-edit-btn"
            onClick={openEdit}
          >
            Edit page
          </button>
        </div>
      ) : null}

      <div className={containerClass} style={{ fontSize: 20, minHeight: '500px' }}>
        {loading ? <p className="text-center text-muted">Loading...</p> : null}
        {!loading && markup ? <PageBody data={staticMarkup} /> : null}
        {!loading && !markup ? (
          <p className="text-center text-muted">Content is not available right now.</p>
        ) : null}
      </div>
      <Footer />

      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        size="xl"
        backdrop="static"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit page</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message?.error ? (
            <div className="alert alert-danger py-2">{message.text}</div>
          ) : null}
          <form id="cms-inline-page-form" onSubmit={onSave}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="cms_p_title">
                  Title
                </label>
                <input
                  id="cms_p_title"
                  className="form-control"
                  required
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => applyTitleToForm(p, e.target.value))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="cms_p_label">
                  Label
                </label>
                <input
                  id="cms_p_label"
                  className="form-control"
                  value={form.menu_label}
                  placeholder="Menu label"
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_label: e.target.value }))
                  }
                />
                <div className="form-text">Shown in the website menu.</div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="cms_p_sub">
                  Sub Title
                </label>
                <input
                  id="cms_p_sub"
                  className="form-control"
                  value={form.subTitle}
                  onChange={(e) => setForm((p) => ({ ...p, subTitle: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="cms_p_url">
                  Page URL
                </label>
                <div className="input-group">
                  <span className="input-group-text">/</span>
                  <input
                    id="cms_p_url"
                    className="form-control"
                    value={form.page_url}
                    placeholder="contact"
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        page_url: sanitizePageUrlInput(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="form-text">
                  Final URL: <code>{urlPreview}</code>
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="cms_p_status">
                  Status
                </label>
                <select
                  id="cms_p_status"
                  className="form-control"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="cms_p_menu">
                  Menu Location
                </label>
                <select
                  id="cms_p_menu"
                  className="form-control"
                  value={form.menu_location}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_location: e.target.value }))
                  }
                >
                  <option value="none">None</option>
                  <option value="header">Header</option>
                  <option value="footer">Footer</option>
                </select>
              </div>
              <div className="col-12 mb-3">
                <label className="form-label" htmlFor="cms_p_content">
                  Content
                </label>
                <RichTextEditor
                  id="cms_p_content"
                  value={form.content}
                  onChange={(html) => setForm((p) => ({ ...p, content: html }))}
                  placeholder="Write page content…"
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowEdit(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cms-inline-page-form"
            className="btn btn-primary cms-page-edit-btn"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save page'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CmsPageScreen
